import { z } from "zod";
import { LOCALE_CODES } from "../i18n/locales.ts";
import { PERMISSION_KEYS } from "../catalog/permissions.ts";
import { PSI_PERMISSION_KEYS } from "../catalog/psi-permissions.ts";
import { ORDER_SOURCES } from "./store.ts";

/** Contracts for the store operations surface (`05-merchant-backoffice.md` §§ 5, 16–20, 25–26, 40). */

const PositiveMinorUnits = z.string().regex(/^\d+$/);

// ── Staff ───────────────────────────────────────────────────────────────────

export const StoreUserSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  username: z.string(),
  number: z.string().nullable(),
  telephones: z.array(z.string()),
  note: z.string().nullable(),
  roleId: z.uuid().nullable(),
  roleName: z.string().nullable(),
  level: z.number().int(),
  /** Independent of the role: may this person operate the till at all. */
  isAllowCashRegister: z.boolean(),
  /** Which table areas this person serves. */
  areaResponsibilityType: z.number().int(),
  isPsiWhiteList: z.boolean(),
  /**
   * Null when there is no photo. Otherwise a cache-busting token for
   * `GET /store/staff/{id}/photo?v=<photoVersion>` — not a URL itself, since
   * the sub-resource route is what serves the bytes.
   */
  photoVersion: z.string().nullable(),
  status: z.enum(["active", "disabled"]),
  createdAt: z.string(),
});
export type StoreUser = z.infer<typeof StoreUserSchema>;

export const CreateStoreUserSchema = z.object({
  name: z.string().min(1).max(120),
  username: z.string().min(2).max(60),
  number: z.string().max(40).nullable().default(null),
  telephones: z.array(z.string()).default([]),
  note: z.string().nullable().default(null),
  roleId: z.uuid().nullable().default(null),
  level: z.number().int().min(0).max(99).default(0),
  isAllowCashRegister: z.boolean().default(false),
  areaResponsibilityType: z.number().int().min(0).default(0),
  isPsiWhiteList: z.boolean().default(false),
});

export const UpdateStoreUserSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  number: z.string().max(40).nullable().optional(),
  telephones: z.array(z.string()).optional(),
  note: z.string().nullable().optional(),
  roleId: z.uuid().nullable().optional(),
  level: z.number().int().min(0).max(99).optional(),
  isAllowCashRegister: z.boolean().optional(),
  areaResponsibilityType: z.number().int().min(0).optional(),
  isPsiWhiteList: z.boolean().optional(),
  status: z.enum(["active", "disabled"]).optional(),
});

/**
 * The bounds are Better Auth's own defaults in effect: `apps/api/src/auth.ts`'s
 * `emailAndPassword` sets neither `minPasswordLength` nor `maxPasswordLength`,
 * so this route never stores a password Better Auth's own routes would refuse.
 */
export const SetStoreUserPasswordSchema = z.object({
  password: z.string().min(8).max(128),
});

// ── Merchant roles ──────────────────────────────────────────────────────────

export const StoreRoleSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isSystem: z.boolean(),
  permissions: z.array(z.string()),
  /** Inventory grants, kept apart from the POS ones (`11-inventory.md` § 2). */
  psiPermissions: z.array(z.string()),
  memberCount: z.number().int(),
  sort: z.number().int(),
});
export type StoreRole = z.infer<typeof StoreRoleSchema>;

export const SaveStoreRoleSchema = z.object({
  code: z
    .string()
    .min(2)
    .max(40)
    .regex(/^[A-Z][A-Z0-9_]*$/)
    .optional(),
  name: z.string().min(1).max(80),
  description: z.string().nullable().default(null),
  permissions: z.array(z.enum(PERMISSION_KEYS)).min(1),
  psiPermissions: z.array(z.enum(PSI_PERMISSION_KEYS)).default([]),
  sort: z.number().int().default(0),
});

// ── Stations ────────────────────────────────────────────────────────────────

/**
 * A station is where the work happens — a hot pass, a bar, a dessert pass —
 * and it is deliberately not a printer. `printerCount` rides along on the list
 * because a station with none prints nothing, and a merchant who cannot see
 * that from the screen will believe a ticket went out that never did.
 */
export const StationSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  sort: z.number().int(),
  printerCount: z.number().int(),
});
export type Station = z.infer<typeof StationSchema>;

export const SaveStationSchema = z.object({
  name: z.string().min(1).max(80),
  sort: z.number().int().default(0),
});

// ── Printers ────────────────────────────────────────────────────────────────

export const PRINTER_ROLES = ["receipt", "station", "label"] as const;
export const PRINTER_CONNECTIONS = ["lan", "bluetooth", "usb", "cloud"] as const;

export const PrinterSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  model: z.string().nullable(),
  connection: z.enum(PRINTER_CONNECTIONS),
  address: z.string().nullable(),
  role: z.enum(PRINTER_ROLES),
  stationId: z.uuid().nullable(),
  /** Resolved from `stationId` so a list renders without a second fetch. */
  stationName: z.string().nullable(),
  copies: z.number().int(),
  status: z.enum(["unbound", "online", "offline", "error"]),
  boundAt: z.string().nullable(),
  lastSeenAt: z.string().nullable(),
  sort: z.number().int(),
});
export type Printer = z.infer<typeof PrinterSchema>;

export const SavePrinterSchema = z.object({
  name: z.string().min(1).max(80),
  model: z.string().nullable().default(null),
  connection: z.enum(PRINTER_CONNECTIONS).default("lan"),
  address: z.string().nullable().default(null),
  role: z.enum(PRINTER_ROLES).default("receipt"),
  stationId: z.uuid().nullable().default(null),
  copies: z.number().int().min(1).max(5).default(1),
  sort: z.number().int().default(0),
});

// ── Print jobs ──────────────────────────────────────────────────────────────

/**
 * What a print job can be.
 *
 * `drawer_kick` prints nothing — it is a pulse down the receipt printer's
 * drawer pin. It is a job because the queue is the only thing that can reach
 * the drawer, and because `08` § Cash drawer calls opening one "the classic
 * theft vector": it has to leave the same trail, with a name against it, that
 * every other document does.
 */
export const DOCUMENT_TYPES = [
  "customer_receipt",
  "station_ticket",
  "label",
  "shift_report",
  "drawer_kick",
] as const;

/**
 * What a *template* can be. A drawer kick has no layout to design, so it is
 * not offered as one — a receipt-template editor with a "drawer" tab would be
 * a tab with nothing in it.
 */
export const TEMPLATE_DOCUMENT_TYPES = [
  "customer_receipt",
  "station_ticket",
  "label",
  "shift_report",
] as const;

export const PrintJobSchema = z.object({
  id: z.uuid(),
  printerId: z.uuid().nullable(),
  printerName: z.string().nullable(),
  documentType: z.enum(DOCUMENT_TYPES),
  subjectRef: z.string().nullable(),
  lang: z.string(),
  renderMode: z.enum(["text", "raster", "auto"]),
  status: z.enum(["queued", "printing", "printed", "failed", "cancelled"]),
  attempts: z.number().int(),
  lastError: z.string().nullable(),
  reprintOfId: z.uuid().nullable(),
  queuedAt: z.string(),
  printedAt: z.string().nullable(),
});
export type PrintJob = z.infer<typeof PrintJobSchema>;

/**
 * Opening the drawer outside a sale (`settlement:drawer`).
 *
 * The reason is required and not optional. `08` § Cash drawer calls this "the
 * classic theft vector", and a no-sale drawer opening with nothing written
 * against it is exactly the event a manager needs to be able to ask about
 * three weeks later.
 */
export const OpenDrawerSchema = z.object({
  reason: z.string().min(1).max(120),
});

// ── Receipt templates ───────────────────────────────────────────────────────

export const ReceiptTemplateSchema = z.object({
  id: z.uuid(),
  documentType: z.enum(TEMPLATE_DOCUMENT_TYPES),
  lang: z.enum(LOCALE_CODES),
  name: z.string(),
  isDefault: z.boolean(),
  renderMode: z.enum(["text", "raster", "auto"]),
  /**
   * What the renderer will actually do, given the language. Derived rather
   * than stored: a complex script cannot print from a thermal printer's
   * built-in codepages, so Khmer resolves to raster whatever `auto` means
   * elsewhere.
   */
  effectiveRenderMode: z.enum(["text", "raster"]),
  lineWidth: z.number().int(),
  showLogo: z.boolean(),
  logoUrl: z.string().nullable(),
  headerText: z.string().nullable(),
  footerText: z.string().nullable(),
  showTaxBreakdown: z.boolean(),
  showTaxRegistration: z.boolean(),
  showItemCodes: z.boolean(),
  showServiceCharge: z.boolean(),
  showSecondCurrency: z.boolean(),
  showQrCode: z.boolean(),
  qrCodeCaption: z.string().nullable(),
  status: z.enum(["enabled", "disabled"]),
});
export type ReceiptTemplate = z.infer<typeof ReceiptTemplateSchema>;

export const SaveReceiptTemplateSchema = z.object({
  documentType: z.enum(TEMPLATE_DOCUMENT_TYPES),
  lang: z.enum(LOCALE_CODES),
  name: z.string().min(1).max(80),
  isDefault: z.boolean().default(false),
  renderMode: z.enum(["text", "raster", "auto"]).default("auto"),
  lineWidth: z.number().int().min(24).max(96).default(48),
  showLogo: z.boolean().default(true),
  logoUrl: z.string().nullable().default(null),
  headerText: z.string().nullable().default(null),
  footerText: z.string().nullable().default(null),
  showTaxBreakdown: z.boolean().default(true),
  showTaxRegistration: z.boolean().default(false),
  showItemCodes: z.boolean().default(false),
  showServiceCharge: z.boolean().default(true),
  showSecondCurrency: z.boolean().default(false),
  showQrCode: z.boolean().default(false),
  qrCodeCaption: z.string().nullable().default(null),
  status: z.enum(["enabled", "disabled"]).default("enabled"),
});

/** A rendered preview, so the editor shows what will actually print. */
export const ReceiptPreviewSchema = z.object({
  lines: z.array(z.string()),
  renderMode: z.enum(["text", "raster"]),
  lineWidth: z.number().int(),
  /**
   * Why raster, as a message key and its parameters. Not a sentence: this is
   * copy a merchant reads, so the client translates it.
   */
  rasterReason: z
    .object({ key: z.enum(["complexScript", "templateChoice"]), script: z.string().optional() })
    .nullable(),
});

// ── Banners ─────────────────────────────────────────────────────────────────

export const BannerSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  imageUrl: z.string(),
  linkUrl: z.string().nullable(),
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
  sort: z.number().int(),
  status: z.enum(["enabled", "disabled"]),
  /** Whether it is showing right now, given its schedule and status. */
  isLive: z.boolean(),
});
export type Banner = z.infer<typeof BannerSchema>;

export const SaveBannerSchema = z
  .object({
    name: z.string().min(1).max(120),
    imageUrl: z.string().min(1),
    linkUrl: z.string().nullable().default(null),
    startsAt: z.iso.datetime().nullable().default(null),
    endsAt: z.iso.datetime().nullable().default(null),
    sort: z.number().int().default(0),
    status: z.enum(["enabled", "disabled"]).default("enabled"),
  })
  .refine((v) => !v.startsAt || !v.endsAt || new Date(v.endsAt) > new Date(v.startsAt), {
    message: "The schedule must end after it starts",
    path: ["endsAt"],
  });

// ── Notifications ───────────────────────────────────────────────────────────

/**
 * The events a store can be told about. Held as a catalog so the two screens
 * and the realtime channel agree on what exists.
 */
export const NOTIFICATION_EVENTS = [
  "order.new",
  "order.paid",
  "order.cancelled",
  "order.refunded",
  "print.failed",
  "shift.warning",
  "stock.sold_out",
] as const;

export const NotificationConfigSchema = z.object({
  id: z.uuid(),
  channel: z.enum(["push", "voice"]),
  eventKey: z.enum(NOTIFICATION_EVENTS),
  isEnabled: z.boolean(),
  targetRoleIds: z.array(z.uuid()),
  lang: z.string().nullable(),
  repeatCount: z.number().int(),
});
export type NotificationConfig = z.infer<typeof NotificationConfigSchema>;

export const SaveNotificationConfigsSchema = z.object({
  configs: z.array(
    z.object({
      channel: z.enum(["push", "voice"]),
      eventKey: z.enum(NOTIFICATION_EVENTS),
      isEnabled: z.boolean(),
      targetRoleIds: z.array(z.uuid()).default([]),
      lang: z.enum(LOCALE_CODES).nullable().default(null),
      repeatCount: z.number().int().min(1).max(5).default(1),
    }),
  ),
});

// ── Operation log ───────────────────────────────────────────────────────────

export const OperationLogSchema = z.object({
  id: z.uuid(),
  actorName: z.string(),
  actorKind: z.string(),
  action: z.string(),
  entity: z.string(),
  entityId: z.uuid().nullable(),
  entityLabel: z.string().nullable(),
  changedFields: z.array(z.string()),
  createdAt: z.string(),
});

// ── Trading configuration ───────────────────────────────────────────────────

export const TRADING_STATUSES = ["open", "temporarily_closed", "permanently_closed"] as const;

export const TradingConfigSchema = z.object({
  introduction: z.string().nullable(),
  kioskId: z.string().nullable(),
  longitude: z.string().nullable(),
  latitude: z.string().nullable(),
  orderingDistanceLimitM: z.number().int().nullable(),
  openTime: z.string(),
  closeTime: z.string(),
  tradingStatus: z.enum(TRADING_STATUSES),
  payFirst: z.boolean(),
  requirePaxCount: z.boolean(),
  cartSharing: z.boolean(),
  takeawaySupportsDelivery: z.boolean(),
  deliveryFee: z.string(),
  autoCancelMinutes: z.number().int(),
  displayTables: z.boolean(),
  showItemImagesOnTill: z.boolean(),
  showItemSalesToDiners: z.boolean(),
  showRemainingStock: z.boolean(),
  pickupNumberFrom: z.number().int(),
  pickupNumberTo: z.number().int(),
  pickupNumberIntervalMinutes: z.number().int(),
  voiceBroadcast: z.boolean(),
  autoPrintCheckoutReceipt: z.boolean(),
  staffTerminalPrintsDirect: z.boolean(),
  /**
   * Which channels accept an order without anyone pressing anything
   * (`STORE_SETTINGS_AUTO_ACCEPT_ORDER`). Empty means none, which is the
   * default: an order nobody looked at is an order nobody is cooking.
   */
  autoAcceptChannels: z.array(z.string()),
  /** The bands this store's market imposes, so a form need not hard-code them. */
  limits: z.object({
    pickupNumberMin: z.number().int().nullable(),
    pickupNumberMax: z.number().int().nullable(),
    orderingDistanceMinM: z.number().int(),
  }),
});
export type TradingConfig = z.infer<typeof TradingConfigSchema>;

export const SaveTradingConfigSchema = z
  .object({
    introduction: z.string().nullable().optional(),
    kioskId: z.string().nullable().optional(),
    longitude: z.string().nullable().optional(),
    latitude: z.string().nullable().optional(),
    orderingDistanceLimitM: z.number().int().nullable().optional(),
    openTime: z
      .string()
      .regex(/^\d{2}:\d{2}(:\d{2})?$/)
      .optional(),
    closeTime: z
      .string()
      .regex(/^\d{2}:\d{2}(:\d{2})?$/)
      .optional(),
    tradingStatus: z.enum(TRADING_STATUSES).optional(),
    payFirst: z.boolean().optional(),
    requirePaxCount: z.boolean().optional(),
    cartSharing: z.boolean().optional(),
    takeawaySupportsDelivery: z.boolean().optional(),
    deliveryFee: PositiveMinorUnits.optional(),
    autoCancelMinutes: z.number().int().min(0).max(1440).optional(),
    displayTables: z.boolean().optional(),
    showItemImagesOnTill: z.boolean().optional(),
    showItemSalesToDiners: z.boolean().optional(),
    showRemainingStock: z.boolean().optional(),
    pickupNumberFrom: z.number().int().min(0).optional(),
    pickupNumberTo: z.number().int().min(0).optional(),
    pickupNumberIntervalMinutes: z.number().int().min(0).optional(),
    voiceBroadcast: z.boolean().optional(),
    autoPrintCheckoutReceipt: z.boolean().optional(),
    staffTerminalPrintsDirect: z.boolean().optional(),
    autoAcceptChannels: z.array(z.enum(ORDER_SOURCES)).optional(),
  })
  .refine(
    (v) =>
      v.pickupNumberFrom === undefined ||
      v.pickupNumberTo === undefined ||
      v.pickupNumberTo >= v.pickupNumberFrom,
    { message: "The pick-up range must end at or above where it starts", path: ["pickupNumberTo"] },
  );

export const TradingHourChangeSchema = z.object({
  id: z.uuid(),
  oldOpenTime: z.string().nullable(),
  oldCloseTime: z.string().nullable(),
  newOpenTime: z.string(),
  newCloseTime: z.string(),
  changedByName: z.string().nullable(),
  createdAt: z.string(),
});

/**
 * Claiming print jobs.
 *
 * A **queue**, not a list. Two tills polling the same store must not both print
 * the same ticket, so claiming moves a job to `printing` in the same statement
 * that selects it — a read-then-write would hand one ticket to both.
 */
export const ClaimPrintJobsSchema = z.object({
  /** Only this printer's work. A till claims for the devices it is bound to. */
  printerIds: z.array(z.uuid()).min(1).max(20),
  limit: z.number().int().min(1).max(20).default(5),
});

export const ClaimedPrintJobSchema = z.object({
  id: z.uuid(),
  printerId: z.uuid().nullable(),
  documentType: z.string(),
  subjectRef: z.string().nullable(),
  payload: z.unknown(),
  renderMode: z.enum(["auto", "text", "raster"]),
  lang: z.string(),
  attempts: z.number().int(),
});

/** How a claimed job ended. A failure carries why, and counts against retries. */
export const SettlePrintJobSchema = z.object({
  outcome: z.enum(["printed", "failed"]),
  error: z.string().max(240).nullable().default(null),
});
